/**
 * CameraService - Uses ACTUAL preview dimensions from plugin
 *
 * The plugin returns actual preview rect: {x, y, width, height}
 * We use this to correctly map screen coordinates to image coordinates.
 */

import { Capacitor } from '@capacitor/core';
import { CameraPreview } from '@capgo/camera-preview';
import CardCamera, { isCardCameraAvailable } from '../native/cardCamera';

const STATE = {
  IDLE: 'idle',
  READY: 'ready',
  CAPTURING: 'capturing',
};

class CameraServiceClass {
  constructor() {
    this.state = STATE.IDLE;
    this.isNative = Capacitor.isNativePlatform();
    // Android runs the native scanner path (CardCamera). iOS keeps the
    // camera-preview plugin. If CardCamera fails to start, Android falls
    // back to camera-preview for that session.
    this.useCardCamera = isCardCameraAvailable();
    this.activeCardCamera = false;
    this.frameListener = null;
    this.readyListener = null;
    this.webTrack = null;

    // Screen dimensions
    this.screenWidth = 0;
    this.screenHeight = 0;

    // ACTUAL preview rect returned by plugin
    this.previewX = 0;
    this.previewY = 0;
    this.previewWidth = 0;
    this.previewHeight = 0;

  }

  getState() { return this.state; }

  /**
   * HARD RESET: Always stop first, then start fresh
   */
  async show(parentElement = 'scanner-camera-view') {
    console.log('[CameraService] show() - HARD RESET');

    if (!this.isNative) {
      this.state = STATE.READY;
      return;
    }

    // Store screen dimensions
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;

    // ALWAYS STOP FIRST
    console.log('[CameraService] Stopping any existing session...');
    try {
      await CameraPreview.stop();
    } catch (e) {
      console.log('[CameraService] Stop result:', e?.message || 'OK');
    }

    // Delay for native cleanup
    await new Promise(r => setTimeout(r, 150));

    if (this.useCardCamera) {
      try {
        const startedAt = Date.now();
        const rect = await CardCamera.start({ x: 0, y: 0, width: 0, height: 0, autoCapture: false });
        this.previewX = rect.x || 0;
        this.previewY = rect.y || 0;
        this.previewWidth = rect.width || this.screenWidth;
        this.previewHeight = rect.height || this.screenHeight;
        this.activeCardCamera = true;
        this.state = STATE.READY;
        console.log(`[CameraService] CardCamera started in ${Date.now() - startedAt}ms`, rect);
        return;
      } catch (err) {
        console.warn('[CameraService] CardCamera failed, falling back to camera-preview:', err?.message || err);
        this.activeCardCamera = false;
      }
    }

    // Fill the screen.
    //
    // Asked for a width and height, the plugin still defaults the preview to
    // 4:3 and letterboxes it: a band of camera across the middle with black
    // above and below, which is what the scan screen looked like. Given a
    // ratio instead, it sizes the layer to that ratio and fills it, and the
    // capture is cropped to what the layer showed, so the frame on screen is
    // the frame in the photo. The ratio is the screen's own, written in the
    // landscape form the plugin expects and swaps for portrait.
    const screenW = Math.round(window.screen.width);
    const screenH = Math.round(window.screen.height);
    const options = {
      position: 'rear',
      toBack: true,
      parent: parentElement,
      x: 0,
      y: 0,
      aspectRatio: `${screenH}:${screenW}`,
      // Android only sizes the preview to the ratio when it is centred; with a
      // ratio equal to the screen's, centred and full-screen are the same thing.
      positioning: 'center',
      disableAudio: true,
      enableHighResolution: true,
      storeToFile: true,
      enableZoom: true,
      tapToFocus: true,
      focusMode: 'auto',
    };

    console.log('[CameraService] Starting with:', options);
    const startTime = Date.now();

    try {
      // Plugin returns actual preview rect!
      const result = await CameraPreview.start(options);
      const elapsed = Date.now() - startTime;
      console.log(`[CameraService] Plugin started in ${elapsed}ms`);
      console.log('[CameraService] Plugin returned preview rect:', result);

      // Store actual preview dimensions
      if (result && typeof result.x !== 'undefined') {
        this.previewX = result.x;
        this.previewY = result.y;
        this.previewWidth = result.width;
        this.previewHeight = result.height;
      } else {
        // Fallback to requested dimensions
        this.previewX = 0;
        this.previewY = 0;
        this.previewWidth = this.screenWidth;
        this.previewHeight = this.screenHeight;
      }

      console.log('[CameraService] ACTUAL preview:', {
        x: this.previewX,
        y: this.previewY,
        width: this.previewWidth,
        height: this.previewHeight,
      });

      this.state = STATE.READY;
      console.log('[CameraService] READY');

    } catch (err) {
      console.error('[CameraService] Start error:', err);
      throw err;
    }
  }

  /**
   * Stop camera
   */
  async hide() {
    console.log('[CameraService] hide()');
    if (!this.isNative) return;

    this.stopDetection();
    if (this.activeCardCamera) {
      try { await CardCamera.stop(); } catch (e) { console.log('[CameraService] CardCamera stop:', e?.message); }
      this.activeCardCamera = false;
    } else {
      try {
        await CameraPreview.stop();
      } catch (e) {
        console.log('[CameraService] Stop error:', e?.message);
      }
    }

    this.state = STATE.IDLE;
  }

  /** True when this session runs the native Android path. */
  isCardCamera() { return this.activeCardCamera; }

  /**
   * Native detection. The plugin scores the guide against every analysis
   * frame on the device and reports numbers; nothing else crosses the bridge.
   * onFrame gets { detected, edgeCount, coverage, corners, steady }.
   */
  async startDetection(frameRect, onFrame, onCaptureReady) {
    if (!this.activeCardCamera) return false;
    await this.setGuide(frameRect);
    this.stopDetection();
    this.frameListener = await CardCamera.addListener('cardFrame', (ev) => { try { onFrame(ev); } catch {} });
    if (onCaptureReady) {
      this.readyListener = await CardCamera.addListener('captureReady', () => { try { onCaptureReady(); } catch {} });
    }
    return true;
  }

  stopDetection() {
    if (this.frameListener) { this.frameListener.remove().catch(() => {}); this.frameListener = null; }
    if (this.readyListener) { this.readyListener.remove().catch(() => {}); this.readyListener = null; }
  }

  async setGuide(frameRect) {
    if (!this.activeCardCamera || !frameRect) return;
    try {
      await CardCamera.setGuide({ x: frameRect.x, y: frameRect.y, width: frameRect.width, height: frameRect.height });
    } catch (e) {
      console.log('[CameraService] setGuide:', e?.message);
    }
  }

  async setAutoCapture(enabled) {
    if (!this.activeCardCamera) return;
    try { await CardCamera.setAutoCapture({ enabled: Boolean(enabled) }); } catch {}
  }

  /**
   * Map screen tap to sensor coordinates (for focus)
   */
  mapScreenToSensor(screenX, screenY) {
    // Convert screen coords to preview-relative coords
    const previewRelX = screenX - this.previewX;
    const previewRelY = screenY - this.previewY;

    // Normalize to 0-1
    const sensorX = previewRelX / this.previewWidth;
    const sensorY = previewRelY / this.previewHeight;

    return {
      x: Math.max(0, Math.min(1, sensorX)),
      y: Math.max(0, Math.min(1, sensorY)),
    };
  }

  /**
   * Get crop rectangle - uses ACTUAL preview dimensions
   *
   * Frame rect is in screen coordinates.
   * We need to convert to image coordinates based on where the preview actually is.
   */
  getCropRect(frameRect, imageWidth, imageHeight) {
    console.log('[CameraService] === CROP CALCULATION ===');
    console.log(`  Screen: ${this.screenWidth}x${this.screenHeight}`);
    console.log(`  Preview: ${this.previewWidth}x${this.previewHeight} at (${this.previewX}, ${this.previewY})`);
    console.log(`  Image: ${imageWidth}x${imageHeight}`);
    console.log(`  Frame: (${frameRect.x.toFixed(0)}, ${frameRect.y.toFixed(0)}) ${frameRect.width.toFixed(0)}x${frameRect.height.toFixed(0)}`);

    // Convert frame from screen coords to preview-relative coords
    const frameRelX = frameRect.x - this.previewX;
    const frameRelY = frameRect.y - this.previewY;

    console.log(`  Frame relative to preview: (${frameRelX.toFixed(0)}, ${frameRelY.toFixed(0)})`);

    // Scale from preview coords to image coords
    const scaleX = imageWidth / this.previewWidth;
    const scaleY = imageHeight / this.previewHeight;

    console.log(`  Scale: ${scaleX.toFixed(2)}x, ${scaleY.toFixed(2)}y`);

    // Calculate crop rect in image coordinates
    const cropX = frameRelX * scaleX;
    const cropY = frameRelY * scaleY;
    const cropW = frameRect.width * scaleX;
    const cropH = frameRect.height * scaleY;

    // Clamp to image bounds
    const result = {
      x: Math.max(0, Math.round(cropX)),
      y: Math.max(0, Math.round(cropY)),
      width: Math.round(cropW),
      height: Math.round(cropH),
    };

    // Ensure we don't exceed image bounds
    if (result.x + result.width > imageWidth) {
      result.width = imageWidth - result.x;
    }
    if (result.y + result.height > imageHeight) {
      result.height = imageHeight - result.y;
    }

    console.log(`  RESULT: (${result.x}, ${result.y}) ${result.width}x${result.height}`);

    return result;
  }

  /**
   * Focus at screen point
   */
  async focusAtPoint(screenX, screenY) {
    if (this.state !== STATE.READY) return;

    const { x, y } = this.mapScreenToSensor(screenX, screenY);
    console.log(`[FOCUS] Screen (${screenX.toFixed(0)}, ${screenY.toFixed(0)}) -> Sensor (${x.toFixed(3)}, ${y.toFixed(3)})`);

    try {
      if (this.activeCardCamera) await CardCamera.setFocus({ x, y });
      else await CameraPreview.setFocus({ x, y });
    } catch (err) {
      console.log('[FOCUS] Error:', err?.message);
    }
  }

  /**
   * Capture photo.
   *
   * @param {{ maxWidth?: number }} [options] the longest side the frame is
   *   scaled to before it crosses the bridge. Default 1600, which is what the
   *   Reader needs. X-Ray asks for more: see BulkScanner.XRAY_CAPTURE_PX.
   */
  async capture({ maxWidth = 1600 } = {}) {
    if (this.state !== STATE.READY) {
      throw new Error('Camera not ready');
    }

    this.state = STATE.CAPTURING;

    if (this.activeCardCamera) {
      try {
        // Cropped on the device to the detected card, or to the guide.
        // display carries a small margin for the binder photo; embed is the
        // guide exactly, for matching. Both arrive as base64 JPEG.
        const r = await CardCamera.capture({ quality: 90, maxWidth: 1600 });
        this.state = STATE.READY;
        return {
          crops: {
            displayCrop: `data:image/jpeg;base64,${r.display}`,
            embedCrop: `data:image/jpeg;base64,${r.embed}`,
          },
          width: r.width,
          height: r.height,
          ms: r.ms,
        };
      } catch (err) {
        this.state = STATE.READY;
        throw err;
      }
    }

    try {
      // Capped at 1600 wide for the Reader. The sensor gives 3264x2448 and the
      // reader is sent about 100 KB anyway; encoding and moving the full frame
      // across the bridge was most of the tap-to-photo time on a low-end
      // Android.
      //
      // X-Ray is the exception, and the number that decided it: on 508 member
      // photographs, a crop under 700 pixels wide was identified 45% of the
      // time and its collector number read 54% of the time; over 2000 pixels,
      // 72% and 78%. The guide is about two fifths of the frame, so a 1600
      // frame handed X-Ray a 627-pixel card - every one of 201 scans on
      // 09.18.2026 - and a collector number ten pixels tall. The reader on
      // the phone never saw the digits. The cap is the caller's to raise.
      const side = Math.max(1600, Math.min(4096, Math.round(Number(maxWidth) || 1600)));
      const result = await CameraPreview.capture({ quality: 92, width: side, height: side });
      this.state = STATE.READY;
      return { filePath: result.value };
    } catch (err) {
      this.state = STATE.READY;
      throw err;
    }
  }

  /**
   * The browser's video track, when the camera is running in a browser.
   *
   * On the web there is no plugin to ask, so torch and zoom go through the
   * track's own constraints. The page owns the stream, so it hands the track
   * here rather than this service opening a second one.
   */
  attachWebTrack(track) {
    this.webTrack = track || null;
  }

  /**
   * What this camera can actually do, so the screen shows a control only when
   * pressing it would do something.
   *
   * Front-facing cameras and most laptops have no torch. Older Android phones
   * report zoom but not torch, or the reverse. Guessing and drawing both
   * buttons gives a person a flashlight that does nothing, which reads as the
   * app being broken rather than the hardware being plain.
   */
  capabilities() {
    if (this.isNative) {
      // Both plugin paths expose flash and zoom on the rear camera.
      return { torch: true, zoom: true, maxZoom: 5 };
    }
    const caps = this.webTrack?.getCapabilities?.() || {};
    return {
      torch: Boolean(caps.torch),
      zoom: Boolean(caps.zoom),
      maxZoom: caps.zoom?.max || 1,
      minZoom: caps.zoom?.min || 1,
    };
  }

  /** Flash: 'off' | 'on' | 'auto' | 'torch'. */
  async setFlash(mode = 'off') {
    if (!this.isNative) {
      if (!this.webTrack) return;
      try {
        await this.webTrack.applyConstraints({ advanced: [{ torch: mode === 'torch' || mode === 'on' }] });
      } catch (e) {
        console.log('[CameraService] web torch:', e?.message);
      }
      return;
    }
    try {
      if (this.activeCardCamera) await CardCamera.setFlash({ mode });
      else await CameraPreview.setFlashMode({ flashMode: mode });
    } catch (e) {
      console.log('[CameraService] setFlash:', e?.message);
    }
  }

  /** Optical/digital zoom ratio, 1 = none. */
  async setZoom(ratio = 1) {
    if (!this.isNative) {
      if (!this.webTrack) return;
      try {
        await this.webTrack.applyConstraints({ advanced: [{ zoom: ratio }] });
      } catch (e) {
        console.log('[CameraService] web zoom:', e?.message);
      }
      return;
    }
    try {
      if (this.activeCardCamera) await CardCamera.setZoom({ ratio });
      else await CameraPreview.setZoom({ level: ratio });
    } catch (e) {
      console.log('[CameraService] setZoom:', e?.message);
    }
  }

  /**
   * Initialize - no-op, let show() do everything
   */
  async initialize() {
    console.log('[CameraService] initialize() - waiting for show()');
  }

  isReady() { return this.state === STATE.READY; }

  /**
   * Capture a low-res sample frame for detection (best-effort, no throws)
   */
  async captureSample(quality = 25) {
    if (this.state !== STATE.READY || !this.isNative || this.activeCardCamera) return null;
    try {
      const result = await CameraPreview.captureSample({ quality });
      return result.value || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Get the actual preview rect (for UI positioning)
   */
  getPreviewRect() {
    return {
      x: this.previewX,
      y: this.previewY,
      width: this.previewWidth,
      height: this.previewHeight,
    };
  }
}

const CameraService = new CameraServiceClass();
export default CameraService;
